/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from 'react'
import {
  type ApolloError,
  gql,
  type TypedDocumentNode,
  useQuery,
} from '@apollo/client'
import type {
  ComplementaryProductsProduct,
  TransformedProduct,
} from '@velasca/types'
import { handleError } from '@velasca/utils/handle-error'
import { transformProductsSliderData } from '@velasca/utils/transform-products-slider-data'

interface ProductConnectionVariables {
  productHandle: string
  metafieldKey: 'cross_sell_products' | 'similar_products'
}

interface ProductConnectionData {
  product: ComplementaryProductsProduct
}

const GET_RELATED_PRODUCTS: TypedDocumentNode<
  ProductConnectionData,
  ProductConnectionVariables
> = gql`
  query SuggestedProducts($metafieldKey: String!, $productHandle: String) {
    product(handle: $productHandle) {
      isGiftCard
      complementaryProducts: metafield(
        namespace: "custom"
        key: $metafieldKey
      ) {
        references(first: 30) {
          edges {
            node {
              ... on Product {
                id
                handle
                title
                productType
                priceRange {
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                compareAtPriceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                }
                media(first: 15) {
                  edges {
                    node {
                      ... on MediaImage {
                        image {
                          url
                        }
                      }
                    }
                  }
                }
                variants(first: 30) {
                  edges {
                    node {
                      id
                      quantityAvailable
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                }
                color: metafield(namespace: "custom", key: "color") {
                  value
                }
                material: metafield(namespace: "custom", key: "material") {
                  value
                }
                materialShort: metafield(
                  namespace: "custom"
                  key: "material_short"
                ) {
                  value
                }
                customProductType: metafield(
                  namespace: "custom"
                  key: "product_type"
                ) {
                  value
                }
                staffPickIconID: metafield(
                  namespace: "custom"
                  key: "staff_pick_icon_id"
                ) {
                  value
                }
                isNew: metafield(namespace: "custom", key: "is_new") {
                  value
                }
                frontImageIndex: metafield(
                  namespace: "custom"
                  key: "front_image_index"
                ) {
                  value
                }
                hoverImageIndex: metafield(
                  namespace: "custom"
                  key: "hover_image_index"
                ) {
                  value
                }
                collectionBrandID: metafield(
                  namespace: "custom"
                  key: "collection_brand_id"
                ) {
                  value
                }
                collectionBrandName: metafield(
                  namespace: "custom"
                  key: "collection_brand_name"
                ) {
                  value
                }
                seasonID: metafield(namespace: "custom", key: "season_id") {
                  value
                }
                seasonName: metafield(namespace: "custom", key: "season_name") {
                  value
                }
                sole: metafield(namespace: "custom", key: "sole") {
                  value
                }
                seoCaption: metafield(namespace: "custom", key: "seo_caption") {
                  value
                }
                storeExclusive: metafield(
                  namespace: "custom"
                  key: "store_exclusive"
                ) {
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`

interface UseRelatedProductsParams {
  type: 'complementary' | 'similar'
  productHandle: string
  skip?: boolean
}

export const useRelatedProducts = ({
  type,
  productHandle,
  skip = false,
}: UseRelatedProductsParams): {
  data: TransformedProduct[] | undefined
  loading: boolean
  error: ApolloError | undefined
} => {
  const { data, loading, error } = useQuery(GET_RELATED_PRODUCTS, {
    variables: {
      productHandle,
      metafieldKey:
        type === 'complementary' ? 'cross_sell_products' : 'similar_products',
    },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    skip: skip || !productHandle,
  })

  useEffect(() => {
    if (productHandle && !error) {
      return
    }

    handleError(error, {
      message: `Couldn't fetch ${type} products`,
      behaviour: 'throw',
    })
  }, [productHandle, error, type])

  return {
    data:
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      data?.product?.complementaryProducts?.references?.edges &&
      transformProductsSliderData(
        data.product.complementaryProducts.references.edges,
      ),
    loading,
    error,
  }
}
